// Edit / delete / add affordances overlaid on top of read-only renderers.
//
// In reorganize mode the runtime exposes:
//   - `BlockWrap` — drag handle on the left, drop-line indicator above
//     / below on dragover, optional delete button on hover.
//   - `AddBlockRow` — slim inline "+Add block" trigger between any two
//     blocks (and at the top of every section). Opens the floating
//     picker on click.
//   - `BlockPickerPortal` — singleton, rendered once at the runtime
//     root, anchored to whichever trigger opened it.
//
// The wrappers are no-ops when the corresponding affordance is policy-
// denied, so plain previews stay clean even when mounted inside this
// pipeline.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, DragEvent, ReactNode } from 'react'
import { Plus, Trash2, GripVertical, Search, X } from 'lucide-react'
import type { Block } from '@/pages/designers/page/types'
import {
  PICKER_CATEGORY_ORDER, RUNTIME_PICKER_OPTIONS,
  useRuntime,
  type PickerOption,
} from './RuntimeContext'

// ─── EditableText ────────────────────────────────────
// Inline contenteditable wrapper used by text-class blocks. On blur,
// fires onCommit with the current text. Preserves the original styling
// of the rendered text by sharing the same DOM position (the renderer
// is replaced with this element only when editing).

interface EditableTextProps {
  value: string
  onCommit: (next: string) => void
  multiline?: boolean
  style?: CSSProperties
  placeholder?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  displayValue?: string
}

export function EditableText({ value, onCommit, multiline, style, placeholder, onKeyDown, displayValue }: EditableTextProps) {
  const ref = useRef<HTMLDivElement>(null)
  const lastValueRef = useRef(value)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement === el) return
    const text = displayValue ?? value
    if (el.textContent !== text) el.textContent = text
    lastValueRef.current = value
  }, [value, displayValue])

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline={multiline}
      onFocus={(e) => {
        if (displayValue !== undefined && e.currentTarget.textContent !== value) {
          e.currentTarget.textContent = value
        }
        setEditing(true)
      }}
      onBlur={(e) => {
        const next = (e.currentTarget.textContent ?? '').replace(/ /g, ' ')
        if (next !== lastValueRef.current) {
          lastValueRef.current = next
          onCommit(next)
        }
        if (displayValue !== undefined) {
          e.currentTarget.textContent = displayValue
        }
        setEditing(false)
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e)
        if (e.defaultPrevented) return
        if (!multiline && e.key === 'Enter') {
          e.preventDefault()
          ;(e.currentTarget as HTMLDivElement).blur()
        }
      }}
      data-placeholder={placeholder}
      style={{
        outline: 'none',
        borderRadius: 4,
        padding: '2px 4px',
        margin: '-2px -4px',
        background: 'transparent',
        whiteSpace: multiline ? 'pre-wrap' : 'normal',
        cursor: 'text',
        minHeight: '1em',
        ...style,
      }}
    >
      {editing ? value : (displayValue ?? value)}
    </div>
  )
}

// ─── BlockWrap ───────────────────────────────────────
// Wraps a rendered block in reorganize-mode chrome: drag handle on the
// left, drop indicator line above / below during dragover, hover-only
// delete button on the right. Acts as a transparent pass-through when
// reorganize is off OR neither delete nor drag is permitted, so the
// final rendered tree stays clean during normal viewing.

interface BlockWrapProps {
  block: Block
  /** Scope of the surrounding container. Drag-drop is same-scope only. */
  scope: string
  children: ReactNode
}

export function BlockWrap({ block, scope, children }: BlockWrapProps) {
  const runtime = useRuntime()
  const reorganize = runtime.reorganizeMode && runtime.mode === 'document'
  const deletable = runtime.canDelete(block)

  // Outside reorganize mode, only the delete-on-hover wrapper kicks in
  // (matching the previous BlockChrome). Drag/drop is reorganize-only.
  if (!reorganize) {
    if (!deletable) return <>{children}</>
    return <DeleteOnHover block={block}>{children}</DeleteOnHover>
  }

  const isDragSource = runtime.dragSource?.blockId === block.id
  const dragTarget = runtime.dragOverTarget
  const isDragOverBefore = dragTarget?.scope === scope
    && dragTarget?.blockId === block.id
    && dragTarget?.position === 'before'
  const isDragOverAfter = dragTarget?.scope === scope
    && dragTarget?.blockId === block.id
    && dragTarget?.position === 'after'

  const onDragStart = (ev: DragEvent<HTMLDivElement>) => {
    if (!ev.dataTransfer) return
    runtime.startDrag(scope, block.id)
    ev.dataTransfer.effectAllowed = 'move'
    try { ev.dataTransfer.setData('text/plain', block.id) } catch { /* ignore */ }
  }

  const onDragOver = (ev: DragEvent<HTMLDivElement>) => {
    const src = runtime.dragSource
    if (!src || src.scope !== scope || src.blockId === block.id) return
    ev.preventDefault()
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move'
    const rect = ev.currentTarget.getBoundingClientRect()
    const above = (ev.clientY - rect.top) < (rect.height / 2)
    runtime.setDragOver(scope, block.id, above ? 'before' : 'after')
  }

  const onDragLeave = () => {
    runtime.clearDragOver(scope, block.id)
  }

  const onDrop = (ev: DragEvent<HTMLDivElement>) => {
    ev.preventDefault()
    runtime.endDrag(scope, block.id)
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={() => runtime.cancelDrag()}
      style={{
        position: 'relative',
        paddingLeft: 24,
        borderRadius: 6,
        opacity: isDragSource ? 0.4 : 1,
        cursor: 'grab',
      }}
    >
      <span
        aria-hidden="true"
        title="Drag to reorder"
        style={{
          position: 'absolute',
          left: 4,
          top: 4,
          color: 'var(--color-text-secondary)',
          opacity: 0.55,
          userSelect: 'none',
          cursor: 'grab',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <GripVertical size={14} />
      </span>
      {isDragOverBefore && <DropLine position="before" />}
      {children}
      {isDragOverAfter && <DropLine position="after" />}
      {deletable && (
        <button
          type="button"
          aria-label="Delete block"
          onClick={() => {
            if (!window.confirm('Delete this block?')) return
            if (runtime.runtimeAddedIds.has(block.id)) {
              void runtime.removeBlock(block.id)
            } else {
              runtime.tombstoneBlock(block.id)
            }
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: -28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: 999,
            background: 'var(--color-surface, #fff)',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLButtonElement).style.color = '#b91c1c'
            ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#fca5a5'
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-secondary)'
            ;(e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border)'
          }}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  )
}

function DropLine({ position }: { position: 'before' | 'after' }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        height: 3,
        background: 'var(--color-primary, #0070f3)',
        borderRadius: 2,
        pointerEvents: 'none',
        ...(position === 'before' ? { top: -2 } : { bottom: -2 }),
      }}
    />
  )
}

// ─── DeleteOnHover ──────────────────────────────────
// Lightweight wrapper used outside reorganize mode when a block's
// delete affordance is still permitted (currently never — `canDelete`
// is gated on reorganizeMode — but kept in case the policy widens).
function DeleteOnHover({ block, children }: { block: Block; children: ReactNode }) {
  const runtime = useRuntime()
  const [hover, setHover] = useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: 'relative' }}
    >
      {children}
      {hover && (
        <button
          type="button"
          aria-label="Delete block"
          onClick={() => {
            if (!window.confirm('Delete this block?')) return
            if (runtime.runtimeAddedIds.has(block.id)) {
              void runtime.removeBlock(block.id)
            } else {
              runtime.tombstoneBlock(block.id)
            }
          }}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: 6,
            background: 'rgba(220, 38, 38, 0.9)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            zIndex: 2,
          }}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  )
}

// ─── AddBlockRow ─────────────────────────────────────
// Slim inline "+Add block" trigger. Rendered above each entry (and at
// the very top of each section) when reorganize mode is on. Clicking
// opens the floating picker, anchored to the trigger button's rect.

interface AddBlockRowProps {
  scope: string
  afterBlockId: string | null
  parentBlock: Block | null
  /**
   * Ordered ids currently rendered in this scope. Captured at the
   * moment the picker is opened so the new block can be spliced into
   * the right position even when there's no `docOrder` override yet.
   */
  currentOrder: string[]
}

export function AddBlockRow({ scope, afterBlockId, parentBlock, currentOrder }: AddBlockRowProps) {
  const runtime = useRuntime()
  if (!runtime.canAddInto(parentBlock)) return null
  const open = runtime.pickerContext?.scope === scope
    && runtime.pickerContext?.afterBlockId === afterBlockId

  return (
    <div style={{ margin: '2px 0', position: 'relative' }}>
      <button
        type="button"
        onClick={(e) => runtime.openPicker(scope, afterBlockId, currentOrder, e)}
        style={{
          width: '100%',
          padding: open ? '8px 12px' : '4px 12px',
          fontSize: 13,
          fontWeight: 500,
          color: open ? 'var(--color-text)' : 'var(--color-text-secondary)',
          background: open ? 'var(--color-surface, #f9fafb)' : 'transparent',
          border: '1px dashed',
          borderColor: open ? 'var(--color-text-secondary, #9ca3af)' : 'transparent',
          borderRadius: 6,
          cursor: 'pointer',
          opacity: open ? 1 : 0.5,
          transition: 'background 0.12s, color 0.12s, border-color 0.12s, opacity 0.12s, padding 0.12s',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
        onMouseEnter={(e) => {
          const btn = e.currentTarget as HTMLButtonElement
          btn.style.background = 'var(--color-surface, #f9fafb)'
          btn.style.color = 'var(--color-text)'
          btn.style.borderColor = 'var(--color-text-secondary, #9ca3af)'
          btn.style.opacity = '1'
          btn.style.padding = '8px 12px'
        }}
        onMouseLeave={(e) => {
          if (open) return
          const btn = e.currentTarget as HTMLButtonElement
          btn.style.background = 'transparent'
          btn.style.color = 'var(--color-text-secondary)'
          btn.style.borderColor = 'transparent'
          btn.style.opacity = '0.5'
          btn.style.padding = '4px 12px'
        }}
      >
        <Plus size={12} />
        Add block
      </button>
    </div>
  )
}

// ─── BlockPickerPortal ──────────────────────────────
// Singleton picker, rendered once at the runtime root so it escapes
// any `overflow:hidden` ancestor (accordion bodies, columns, etc.).
// Reads its open/anchor/search state from RuntimeContext.

const PICKER_WIDTH = 460
const PICKER_HEIGHT = 460

export function BlockPickerPortal() {
  const runtime = useRuntime()
  const ctx = runtime.pickerContext
  const anchor = runtime.pickerAnchor

  // Page-level whitelist of allowed block types. Empty / undefined =
  // "allow all" — every catalogue entry surfaces.
  const allowedTypes = runtime.page.runtimeAddableTypes
  const filteredOptions = useMemo(() => {
    let opts = RUNTIME_PICKER_OPTIONS
    if (allowedTypes && allowedTypes.length > 0) {
      const set = new Set(allowedTypes)
      opts = opts.filter((o) => set.has(o.type))
    }
    const q = runtime.pickerSearch.trim().toLowerCase()
    if (q) {
      opts = opts.filter((o) =>
        o.label.toLowerCase().includes(q)
          || o.type.toLowerCase().includes(q)
          || o.description.toLowerCase().includes(q),
      )
    }
    return opts
  }, [allowedTypes, runtime.pickerSearch])

  const grouped = useMemo(() => {
    const map = new Map<string, PickerOption[]>()
    for (const opt of filteredOptions) {
      const list = map.get(opt.category) ?? []
      list.push(opt)
      map.set(opt.category, list)
    }
    const out: { category: string; options: PickerOption[] }[] = []
    for (const cat of PICKER_CATEGORY_ORDER) {
      const list = map.get(cat)
      if (list && list.length > 0) out.push({ category: cat, options: list })
    }
    return out
  }, [filteredOptions])

  const onPick = useCallback((opt: PickerOption) => {
    if (!ctx) return
    const parentId = ctx.scope === '__root__' ? null : ctx.scope
    runtime.closePicker()
    void runtime.addBlock({
      type: opt.type,
      parentId,
      afterBlockId: ctx.afterBlockId,
      properties: opt.seed(),
      currentOrder: ctx.currentOrder,
    })
  }, [ctx, runtime])

  // Close on Escape so keyboard users aren't trapped.
  useEffect(() => {
    if (!ctx) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') runtime.closePicker()
    }
    document.addEventListener('keydown', handler)
    return () => { document.removeEventListener('keydown', handler) }
  }, [ctx, runtime])

  if (!ctx) return null
  const left = anchor?.left ?? 0
  const top = anchor?.top ?? 0

  return (
    <>
      <div
        aria-hidden="true"
        onClick={() => runtime.closePicker()}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          background: 'transparent',
        }}
      />
      <div
        role="dialog"
        aria-label="Add a block"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          left,
          top,
          width: PICKER_WIDTH,
          maxWidth: 'calc(100vw - 16px)',
          maxHeight: PICKER_HEIGHT,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-surface, #fff)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <Search size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
          <input
            type="text"
            autoFocus
            placeholder="Search blocks…"
            spellCheck={false}
            autoComplete="off"
            value={runtime.pickerSearch}
            onChange={(e) => runtime.setPickerSearch(e.target.value)}
            style={{
              flex: 1,
              padding: '4px 6px',
              fontSize: 14,
              color: 'var(--color-text)',
              background: 'var(--color-bg, transparent)',
              border: '1px solid transparent',
              borderRadius: 4,
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => runtime.closePicker()}
            aria-label="Close picker"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
              cursor: 'pointer',
            }}
          >
            <X size={12} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '6px 6px 10px', flex: 1 }}>
          {grouped.length === 0 ? (
            <div style={{
              padding: '18px 14px',
              textAlign: 'center',
              color: 'var(--color-text-secondary)',
              fontSize: 13.5,
            }}>
              No blocks match “{runtime.pickerSearch}”
            </div>
          ) : grouped.map((group, gi) => (
            <div
              key={group.category}
              style={{
                padding: '4px 0',
                borderTop: gi === 0 ? 'none' : '1px solid var(--color-border)',
                marginTop: gi === 0 ? 0 : 4,
                paddingTop: gi === 0 ? 4 : 8,
              }}
            >
              <div style={{
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--color-text-secondary)',
              }}>
                {group.category}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {group.options.map((opt) => (
                  <PickerRow key={opt.type} option={opt} onPick={onPick} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function PickerRow({ option, onPick }: { option: PickerOption; onPick: (opt: PickerOption) => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={() => onPick(option)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '7px 10px',
        fontSize: 14,
        textAlign: 'left',
        color: hover ? 'var(--color-on-primary, #fff)' : 'var(--color-text)',
        background: hover ? 'var(--color-primary, #0070f3)' : 'transparent',
        border: '1px solid transparent',
        borderRadius: 6,
        cursor: 'pointer',
      }}
    >
      <span style={{
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 15,
        borderRadius: 4,
        background: hover ? 'rgba(255,255,255,0.18)' : 'var(--color-surface-alt, rgba(0,0,0,0.05))',
      }}>
        {option.icon}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
        <span style={{ fontWeight: 600 }}>{option.label}</span>
        <span style={{
          fontSize: 13,
          color: hover ? 'rgba(255,255,255,0.85)' : 'var(--color-text-secondary)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {option.description}
        </span>
      </span>
    </button>
  )
}

// ─── Back-compat exports ────────────────────────────
// Keep these names so existing imports compile without churn. Both
// route to the new pipeline above; the old BlockChrome was a delete-
// hover wrapper, which DeleteOnHover replaces, and AddBlockAffordance
// is a thin alias around AddBlockRow.

export function BlockChrome({ block, children }: { block: Block; children: ReactNode }) {
  const runtime = useRuntime()
  if (!runtime.canDelete(block)) return <>{children}</>
  return <DeleteOnHover block={block}>{children}</DeleteOnHover>
}

export function AddBlockAffordance({ parentId, afterBlockId }: { parentId: string | null; afterBlockId: string | null }) {
  const runtime = useRuntime()
  const parentBlock = parentId === null ? null : findBlockById(runtime.page, parentId)
  const scope = parentId ?? '__root__'
  // Back-compat path — callers used this for an end-of-page append.
  // We can't recover the natural-order list from here, so pass an
  // empty array; addBlock falls back to that when no docOrder
  // override exists, which lands the new block at the front. Callers
  // that need precise placement should use AddBlockRow directly.
  return <AddBlockRow scope={scope} afterBlockId={afterBlockId} parentBlock={parentBlock} currentOrder={[]} />
}

// Small block lookup by id across the flattened tree. Limited to the
// page template — runtime-added containers aren't expected to nest
// further (no UI exposes that).
function findBlockById(page: import('@/pages/designers/page/types').PageModel, id: string): Block | null {
  const seed = page.sections
    ? page.sections.flatMap((s) => s.blocks)
    : page.blocks
  const stack: Block[] = [...seed]
  while (stack.length) {
    const b = stack.shift()!
    if (b.id === id) return b
    if (b.children && b.children.length) stack.push(...b.children)
  }
  return null
}
